import { Workbook, Worksheet } from 'exceljs';
import { createContractReleaseWorkbook } from './workbookFormatter';
import { ColumnMappings, ColumnMappingsSpec, ContractReleaseRow_PreProcessed, ContractReleaseRow_Processed, ContractReleaseRow_Mappings } from './contractReleaseTypes';

const OTHER_ADJUSTMENTS = ["Customs broker Fee", 'LHL Freight', 'Origination Fee', 'Beacon Pricing Enrollment Fee', 'Deferred Payment Contract Assessment', 'Administration Fee'];
export const OTC_FEE_ADJUSTMENTS = ["OTC Fee", "OTC Fee Exit", "OTC Fee Roll", "OTC Adjustment", "OTC Additional Adjustment"];

function isOtcFeeAdjustment(value: string | null | undefined): boolean {
  if (!value) return false;
  return OTC_FEE_ADJUSTMENTS.some((key) => key.toLowerCase().trim() === value.trim().toLowerCase());
}

export async function processContractReleaseReport(workbook: Workbook, date?: Date): Promise<Workbook> {
  try {
    console.log('Processing Contract Release Report...');

    // Get relevant worksheets using the new function with ContractReleaseRow_Processed type
    const worksheetResults = getRelevantWorksheet<ContractReleaseRow_PreProcessed>(workbook, ContractReleaseRow_Mappings);

    // Array to store processed rows
    const processedRows: ContractReleaseRow_Processed[] = [];

    for (const { worksheet, columnMappings } of worksheetResults) {
      // Skip the header row
      for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber);

        // Read all 5 adjustment values
        const addDedAdjustment1 = row.getCell(columnMappings.addDedAdjustment1).value?.toString() || '';
        const addDedAdjustment2 = row.getCell(columnMappings.addDedAdjustment2).value?.toString() || null;
        const addDedAdjustment3 = row.getCell(columnMappings.addDedAdjustment3).value?.toString() || null;
        const addDedAdjustment4 = row.getCell(columnMappings.addDedAdjustment4).value?.toString() || null;
        const addDedAdjustment5 = row.getCell(columnMappings.addDedAdjustment5).value?.toString() || null;

        // Skip rows where none of the adjustments are in the OTC Fee set
        const hasOtcFeeAdjustment = [addDedAdjustment1, addDedAdjustment2, addDedAdjustment3, addDedAdjustment4, addDedAdjustment5]
          .some(isOtcFeeAdjustment);

        if (!hasOtcFeeAdjustment) {
          continue;
        }

        // Convert row to ContractReleaseRow_PreProcessed object
        const processedRow: ContractReleaseRow_PreProcessed = {
          location: row.getCell(columnMappings.location).value?.toString() || '',
          pso: row.getCell(columnMappings.pso).value?.toString() || '',
          cont: row.getCell(columnMappings.cont).value?.toString() || '',
          contractNo: Number(row.getCell(columnMappings.contractNo).value) || 0,
          status: row.getCell(columnMappings.status).value?.toString() || '',
          name: row.getCell(columnMappings.name).value?.toString() || '',
          commodity: row.getCell(columnMappings.commodity).value?.toString() || '',
          commodityDescription: row.getCell(columnMappings.commodityDescription).value?.toString() || '',
          rel: Number(row.getCell(columnMappings.rel).value) || 0,
          uom: row.getCell(columnMappings.uom).value?.toString() || '',
          quantity: Number(row.getCell(columnMappings.quantity).value) || 0,
          balance: Number(row.getCell(columnMappings.balance).value) || 0,
          addDedRateType: row.getCell(columnMappings.addDedRateType).value?.toString() || '',
          addDedCode1: row.getCell(columnMappings.addDedCode1).value?.toString() || '',
          addDedAdjustment1: addDedAdjustment1,
          addDedAmount1: Number(row.getCell(columnMappings.addDedAmount1).value) || 0,
          addDedCode2: row.getCell(columnMappings.addDedCode2).value?.toString() || null,
          addDedAdjustment2: addDedAdjustment2,
          addDedAmount2: Number(row.getCell(columnMappings.addDedAmount2).value) || 0,
          addDedCode3: row.getCell(columnMappings.addDedCode3).value?.toString() || null,
          addDedAdjustment3: addDedAdjustment3,
          addDedAmount3: Number(row.getCell(columnMappings.addDedAmount3).value) || 0,
          addDedCode4: row.getCell(columnMappings.addDedCode4).value?.toString() || null,
          addDedAdjustment4: addDedAdjustment4,
          addDedAmount4: Number(row.getCell(columnMappings.addDedAmount4).value) || 0,
          addDedCode5: row.getCell(columnMappings.addDedCode5).value?.toString() || null,
          addDedAdjustment5: addDedAdjustment5,
          addDedAmount5: Number(row.getCell(columnMappings.addDedAmount5).value) || 0,
        };

        // Sum only amounts whose corresponding adjustment is an OTC Fee type
        const adjustments = [
          { adjustment: processedRow.addDedAdjustment1, amount: processedRow.addDedAmount1 },
          { adjustment: processedRow.addDedAdjustment2, amount: processedRow.addDedAmount2 },
          { adjustment: processedRow.addDedAdjustment3, amount: processedRow.addDedAmount3 },
          { adjustment: processedRow.addDedAdjustment4, amount: processedRow.addDedAmount4 },
          { adjustment: processedRow.addDedAdjustment5, amount: processedRow.addDedAmount5 },
        ];
        const relevantAmountSum = adjustments
          .filter(({ adjustment }) => isOtcFeeAdjustment(adjustment))
          .reduce((sum, { amount }) => sum + amount, 0);

        // Add processed row to array
        processedRows.push({
          ...processedRow,
          otcFeePremium: relevantAmountSum * processedRow.balance,
        });
      }
    }

    // Create and return the workbook using the processed rows
    return createContractReleaseWorkbook(processedRows, date);
  } catch (error) {
    console.error('Error processing contract release report:', error);
    throw error;
  }
}

export interface WorksheetResult<T> {
  worksheet: Worksheet,
  columnMappings: ColumnMappings<T>,
}

export function testWorksheetColumns<T>(worksheet: Worksheet, columnsSpec: ColumnMappingsSpec<T>) {
  // Get the first row which should contain headers
  const headerRow = worksheet.getRow(1);

  const result = {
    found: {} as Partial<ColumnMappings<T>>,
    fullColumnMappings: null as ColumnMappings<T> | null,
    missing: [] as (keyof T)[],
    isMatch: false,
    mismatchMessage: null as string | null,
  }

  for (let key of Object.keys(columnsSpec)) {
    const { validColumns, validNames } = columnsSpec[key as (keyof T)];
    const normalizedValidNames = validNames.map(name => name.toString().toLowerCase().trim());

    let columnIsMissing = true;
    for (let column of validColumns) {
      const headerName = headerRow.getCell(column).value?.toString().toLowerCase().trim();
      if (headerName && normalizedValidNames.includes(headerName)) {
        // Match for column found
        result.found[key as (keyof T)] = column;
        columnIsMissing = false;
        break;
      }
    }

    if (columnIsMissing) {
      result.missing.push(key as (keyof T));
    }
  }

  result.isMatch = result.missing.length == 0;
  if (result.isMatch) {
    // If result.isMatch, that means all the columns were found, and even though the type is Partial<>, all of the fields are accounted for
    result.fullColumnMappings = result.found as ColumnMappings<T>;
  }
  return result;
}

export function getRelevantWorksheet<T>(workbook: Workbook, columnsSpec: ColumnMappingsSpec<T>): WorksheetResult<T>[] {
  const matchingWorksheets: WorksheetResult<T>[] = [];

  workbook.worksheets.forEach(worksheet => {
    const result = testWorksheetColumns(worksheet, columnsSpec);

    if (!result.isMatch) {
      console.warn(`Warning: Worksheet "${worksheet.name}" does not match the expected format. ${result.mismatchMessage ?? ""}`, result);
    }
    else {
      const columnMappings = result.fullColumnMappings!;
      matchingWorksheets.push({
        worksheet,
        columnMappings,
      });
    }
  });

  if (matchingWorksheets.length !== 1) {
    console.warn(`Warning: Found ${matchingWorksheets.length} worksheets matching the required format. Expected 1.`);
  }

  return matchingWorksheets;
}
